import {
  getServerLocale,
  initServerTranslation,
} from '@bts-web/utils-lokalise';
import {
  getTradeTranslations,
  TradeControllerVariants,
  TradeOperation,
} from '../../utils';
import { getTranslationsPerTradeType } from './utils/getTranslationsPerTradeType';
import {
  NavigationBackButton,
  NavigationButton,
} from '../../../navigation/index';
import { getAssetById, NavigationBar } from '../../../common';
import { MainTradeDataFlowWrapper } from '../MainTradeDataFlowWrapper/MainTradeDataFlowWrapper';
import { TFunction } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { getSwapAssetsDataAndExchangeRate } from '../../../common/gqlActions/getSwapAssetsDataAndExchangeRate';
import { PrecisionFloat } from '@bts-web/utils-formatting';
import { getUnstakeAssetAcceptData } from '../../../common/gqlActions';
import { PriceTypeDrawerButton } from '../PriceTypeDrawerButton/PriceTypeDrawerButton';
import { TARGET_PRICE_KEY } from '../constants';
import { OrderOperation, TradePriceType } from '../../../orders/types';
import { WithServerSpanHOC } from '@bts-web/utils-next-infrastructure/monitoring';
import { GenericErrorMessage } from '../../../layouts/GenericErrorMessage/GenericErrorMessage';
import { AppropriatenessCheckGuard } from '../components/AppropriatenessCheckGuard';
import { getServerFeatureFlag } from '../../../statsig/sdk/server';

export interface TradeControllerPropsBase {
  skipCheckAppropriateness?: boolean;
  routeParams: Record<string, string>;
  searchParams?: Record<string, string>;
}

const TradeController = async ({
  skipCheckAppropriateness,
  routeParams,
  searchParams,
}: TradeControllerPropsBase) => {
  const { assetId, operation } = routeParams;

  const { swapWith } = searchParams ?? {};

  const targetAssetId =
    operation === TradeOperation.swap ? swapWith : undefined;

  const orderOperation: OrderOperation | undefined =
    operation === TradeOperation.order
      ? (searchParams?.orderType as OrderOperation) || OrderOperation.BUY
      : undefined;

  const isOrderTargetPricePage =
    operation === TradeOperation.order &&
    TARGET_PRICE_KEY in (searchParams || {});

  const { t } = await initServerTranslation();

  const feature_hasOrdersEnabled = await getServerFeatureFlag(
    'feature_hasOrdersEnabled',
  );

  const ordersEnabled = feature_hasOrdersEnabled;

  const { data: assetsResponse } = targetAssetId
    ? await getSwapAssetsDataAndExchangeRate({
        sourceAssetId: assetId,
        targetAssetId,
      })
    : await getAssetById(assetId);

  if (!assetsResponse) {
    console.error('[TradeController] Failed to fetch asset data', {
      assetId,
      targetAssetId,
    });

    return <GenericErrorMessage />;
  }

  const sourceAsset = targetAssetId
    ? (
        assetsResponse as NonNullable<
          Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
        >
      ).asset
    : (assetsResponse as Awaited<ReturnType<typeof getAssetById>>['data']);

  const targetAsset = targetAssetId
    ? (
        assetsResponse as NonNullable<
          Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
        >
      ).targetAsset
    : undefined;

  if (!sourceAsset || (targetAssetId && !targetAsset)) {
    return <GenericErrorMessage />;
  }

  const currentLocale = await getServerLocale();

  const translations = getTradeTranslations(t as TFunction);

  if (operation === TradeOperation.order) {
    translations.afterFeesAtCurrentPrice = t(
      'after_fees_at_current_price' as 'after_fees',
      { defaultValue: 'after fees at current price' },
    );

    translations.afterSpreadAtCurrentPrice = t(
      'after_spread_at_current_price' as 'after_spread',
      { defaultValue: 'after spread at current price' },
    );
  }

  let stakedAsset: PrecisionFloat | undefined;

  let stakedFiat: PrecisionFloat | undefined;

  let rewardAsset: PrecisionFloat | undefined;

  let rewardFiat: PrecisionFloat | undefined;

  if (operation === TradeOperation.unstake) {
    const { data } = await getUnstakeAssetAcceptData({
      assetId,
    });

    const unstakeAssetData = data.unstakeAsset?.items?.[0] ?? null;

    if (unstakeAssetData) {
      stakedAsset = unstakeAssetData.stakedAsset;

      stakedFiat = unstakeAssetData.stakedFiat;

      rewardAsset = unstakeAssetData.rewardsAsset;

      rewardFiat = unstakeAssetData.rewardsFiat;
    }
  }

  const { translatedInnerValues } = getTranslationsPerTradeType({
    assetType: sourceAsset.__typename,
    translations,
    variant: operation as TradeControllerVariants,
  });

  const getOrderVariantTitle = (): string | React.ReactElement => {
    if (!ordersEnabled) {
      return '';
    }

    if (isOrderTargetPricePage) {
      return orderOperation === OrderOperation.BUY
        ? translations.buyStopTitle
        : translations.sellStopTitle;
    }

    return (
      <PriceTypeDrawerButton
        assetId={assetId}
        orderOperation={orderOperation as OrderOperation}
        priceType={TradePriceType.STOP}
      />
    );
  };

  const orderVariantTitle = getOrderVariantTitle();

  const variantsTitles: Record<
    TradeControllerVariants,
    string | React.ReactElement
  > = {
    buy: ordersEnabled ? (
      <PriceTypeDrawerButton
        assetId={assetId}
        orderOperation={OrderOperation.BUY}
        priceType={TradePriceType.MARKET}
      />
    ) : (
      translations.buy
    ),
    sell: ordersEnabled ? (
      <PriceTypeDrawerButton
        assetId={assetId}
        orderOperation={OrderOperation.SELL}
        priceType={TradePriceType.MARKET}
      />
    ) : (
      translations.sell
    ),
    savings: translations.savingsPlan,
    swap: translations.swap,
    stake: translations.stake,
    unstake: translations.unstake,
    order: orderVariantTitle,
  };

  const { currency } = getAppConfig();

  const initialExchangeRate = (
    assetsResponse as NonNullable<
      Awaited<ReturnType<typeof getSwapAssetsDataAndExchangeRate>>['data']
    >
  ).exchangeRate;

  return (
    <AppropriatenessCheckGuard
      assetType={targetAsset ? targetAsset.__typename : sourceAsset.__typename}
      operation={operation}
      assetId={assetId}
      searchParams={searchParams}
      skipCheckAppropriateness={skipCheckAppropriateness}
    >
      <NavigationBar
        title={variantsTitles[operation as TradeControllerVariants]}
        NavigationLeftSlot={<NavigationBackButton />}
        NavigationRightSlot={
          <NavigationButton to="/portfolio" ariaLabel="close trade" />
        }
      />

      <MainTradeDataFlowWrapper
        initialData={{
          assetData: sourceAsset,
          ...(operation === TradeOperation.unstake && stakedAsset && stakedFiat
            ? {
                stakedAsset,
                stakedFiat,
                rewardAsset,
                rewardFiat,
              }
            : {}),
          ...(targetAssetId && targetAsset
            ? {
                targetAssetData: targetAsset,
                initialSwapExchangeRate: initialExchangeRate,
              }
            : {}),
        }}
        staticData={{
          currentLocale,
          translations,
          variant: operation as TradeControllerVariants,
          currency,
          translatedInnerValues: translatedInnerValues,
          orderOperation: orderOperation,
        }}
      />
    </AppropriatenessCheckGuard>
  );
};

export const TRADE_PAGE_RENDER_SPAN_NAME = 'trade.page.render';

const TradeControllerWithSpan = WithServerSpanHOC(
  TRADE_PAGE_RENDER_SPAN_NAME,
  TradeController,
);

export { TradeControllerWithSpan as TradeController };
